import moment from "moment";
import { normalize } from "normalizr";
import { v4 } from "uuid";
import * as schemaProjects from "~/schemas/schema.projects.jsx";
import * as schemaTasks from "~/schemas/schema.tasks.jsx";
import * as schemaTimeRecords from "~/schemas/schema.time_records.jsx";
import { arrayOfTimeRecords } from "~/schemas/schema.time_records.jsx";
import * as actionTypes from "~/store/action_types";
import Angie from "~/utils/angie.jsx";
import { dateToMySQLFormat } from "~/utils/date.jsx";
import { fetch } from "~/utils/fetch.jsx";
import { encodeData } from "~/utils/utils.jsx";

export const fetchProjectTimeRecordsForList = (projectId, params = {}) => {
  const query = encodeData(params);
  return {
    types: [
      actionTypes.FETCH_PROJECT_LIST_TIME_RECORDS_LOADING,
      actionTypes.FETCH_PROJECT_LIST_TIME_RECORDS_SUCCESS,
      actionTypes.FETCH_PROJECT_LIST_TIME_RECORDS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/time-records/for-screen?${query}`,
        withHeaders: true,
      }),
    payload: {
      schema: {
        related: {
          Project: schemaProjects.arrayOfProjects,
          Task: schemaTasks.arrayOfTasks,
        },
        time_records: schemaTimeRecords.arrayOfTimeRecords,
      },
      cursor: params?.cursor,
    },
  };
};

export const fetchProjectTimeRecordsByDates = (projectId, from, to) => {
  const params = {
    from,
    to,
  };

  const query = encodeData(params);
  return {
    types: [
      actionTypes.FETCH_PROJECT_TIME_RECORDS_BY_DATES_LOADING,
      actionTypes.FETCH_PROJECT_TIME_RECORDS_BY_DATES_SUCCESS,
      actionTypes.FETCH_PROJECT_TIME_RECORDS_BY_DATES_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/time-records/filtered-by-date?${query}`,
      }),
    payload: {
      schema: {
        related: {
          Project: schemaProjects.arrayOfProjects,
          Task: schemaTasks.arrayOfTasks,
        },
        time_records: schemaTimeRecords.arrayOfTimeRecords,
      },
    },
  };
};

export const addTimeRecordForTask = (projectId, attributes) => {
  const { record_date, value, billable_status } = attributes;

  if (typeof value === "string") {
    attributes.value = Number(value);
  }

  if (typeof billable_status === "boolean") {
    attributes.billable_status = Number(billable_status);
  }

  const body = { ...attributes };

  if (typeof record_date === "number") {
    body.record_date = dateToMySQLFormat(record_date);
  }

  attributes.created_on = moment.utc().unix();

  return {
    types: [
      actionTypes.ADD_TIME_RECORD_LOADING,
      actionTypes.ADD_TIME_RECORD_SUCCESS,
      actionTypes.ADD_TIME_RECORD_ERROR,
    ] /* eslint-disable */,
    callAPI: () =>
      fetch({
        /* eslint-enable */
        url: `${Angie.api_url}/projects/${projectId}/time-records`,
        method: `POST`,
        body,
      }),
    payload: {
      attributes,
      id: v4(),
      schema: {
        single: schemaTimeRecords.timeRecord,
      },
    },
  };
};

export const editTimeRecord = (projectId, timeRecordId, attrs) => {
  return {
    types: [
      actionTypes.EDIT_TIME_RECORD_LOADING,
      actionTypes.EDIT_TIME_RECORD_SUCCESS,
      actionTypes.EDIT_TIME_RECORD_ERROR,
    ] /* eslint-disable */,
    callAPI: () =>
      fetch({
        /* eslint-enable */
        url: `${Angie.api_url}/projects/${projectId}/time-records/${timeRecordId}`,
        method: `PUT`,
        body: attrs,
      }),
    payload: {
      schema: {
        single: schemaTimeRecords.timeRecord,
      },
    },
  };
};

export const deleteTimeRecord = (timeRecordId) => {
  return {
    types: [
      actionTypes.DELETE_TIME_RECORD_LOADING,
      actionTypes.DELETE_TIME_RECORD_SUCCESS,
      actionTypes.DELETE_TIME_RECORD_ERROR,
    ] /* eslint-disable */,
    callAPI: () =>
      fetch({
        /* eslint-enable */
        url: `${Angie.api_url}/move-to-trash/time-record/${timeRecordId}`,
        method: `PUT`,
      }),
    payload: {
      timeRecordId,
      schema: {
        single: schemaTimeRecords.timeRecord,
      },
    },
  };
};

const timeRecordFromEventStream = (data = {}, type) => {
  return {
    type,
    response: {
      single: data,
    },
    schema: {
      single: schemaTimeRecords.timeRecord,
    },
  };
};

export const timeRecordCreatedFromEventStream = (data) => {
  return timeRecordFromEventStream(data, actionTypes.TIME_RECORD_CREATED_FROM_EVENT_STREAM);
};

export const timeRecordUpdatedFromEventStream = (data) => {
  return timeRecordFromEventStream(data, actionTypes.TIME_RECORD_UPDATED_FROM_EVENT_STREAM);
};

export const timeRecordMovedToTrashFromEventStream = (data) => {
  return timeRecordFromEventStream(data, actionTypes.TIME_RECORD_MOVE_TO_TRASH_FROM_EVENT_STREAM);
};

export const timeRecordRestoredFromTrashFromEventStream = (data) => {
  return timeRecordFromEventStream(data, actionTypes.TIME_RECORD_RESTORED_FROM_TRASH_FROM_EVENT_STREAM);
};

export const timeRecordMovedToProjectFromEventStream = (data) => {
  return timeRecordFromEventStream(data, actionTypes.TIME_RECORD_MOVED_TO_PROJECT_FROM_EVENT_STREAM);
};

export const fetchTimeRecords = (data) => {
  const query = encodeData(data);
  return {
    types: [
      actionTypes.FETCH_TIMESHEET_TIME_RECORDS_LOADING,
      actionTypes.FETCH_TIMESHEET_TIME_RECORDS_SUCCESS,
      actionTypes.FETCH_TIMESHEET_TIME_RECORDS_ERROR,
    ] /* eslint-disable */,
    callAPI: () =>
      fetch({
        /* eslint-enable */ url: `${Angie.api_url}/time-records?${query}`,
      }),
    payload: {
      schema: {
        related: {
          Project: schemaProjects.arrayOfProjects,
          Task: schemaTasks.arrayOfTasks,
        },
        time_records: schemaTimeRecords.arrayOfTimeRecords,
      },
    },
  };
};

export const fetchTimeRecordsForUser = (userId, data) => {
  const query = encodeData(data);
  return {
    types: [
      actionTypes.FETCH_TIMESHEET_TIME_RECORDS_LOADING,
      actionTypes.FETCH_TIMESHEET_TIME_RECORDS_SUCCESS,
      actionTypes.FETCH_TIMESHEET_TIME_RECORDS_ERROR,
    ] /* eslint-disable */,
    callAPI: () =>
      fetch({
        /* eslint-enable */
        url: `${Angie.api_url}/users/${userId}/time-records/filtered-by-date?${query}`,
      }),
    payload: {
      schema: {
        related: {
          Project: schemaProjects.arrayOfProjects,
          Task: schemaTasks.arrayOfTasks,
        },
        time_records: schemaTimeRecords.arrayOfTimeRecords,
      },
    },
  };
};

export const fetchParentTimeRecords = (parentType, parentId, projectId, queryData = {}) => {
  const query = encodeData({
    location: parentType,
    parent: parentId,
    ...queryData,
  });

  return {
    types: [
      actionTypes.FETCH_TIME_RECORDS_LOADING,
      actionTypes.FETCH_TIME_RECORDS_SUCCESS,
      actionTypes.FETCH_TIME_RECORDS_ERROR,
    ] /* eslint-disable */,
    callAPI: () =>
      fetch({
        /* eslint-enable */
        url: `${Angie.api_url}/projects/${projectId}/time-records/for-screen?${query}`,
        withHeaders: true,
      }),
    payload: {
      parentType,
      parentId,
      schema: {
        related: {
          Project: schemaProjects.arrayOfProjects,
          Task: schemaTasks.arrayOfTasks,
        },
        time_records: schemaTimeRecords.arrayOfTimeRecords,
      },
    },
  };
};

export const moveTimeRecord = (projectId, id, attributes) => {
  return {
    types: [
      actionTypes.MOVE_TIME_RECORD_LOADING,
      actionTypes.MOVE_TIME_RECORD_SUCCESS,
      actionTypes.MOVE_TIME_RECORD_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/time-records/${id}/move`,
        method: `put`,
        body: attributes,
      }),
    payload: {
      projectId,
      id,
      ...attributes,
      schema: {
        single: schemaTimeRecords.timeRecord,
      },
    },
  };
};

export const batchEditTimeRecords = (projectId, ids, attributes) => {
  return {
    types: [
      actionTypes.BATCH_EDIT_TIME_RECORDS_LOADING,
      actionTypes.BATCH_EDIT_TIME_RECORDS_SUCCESS,
      actionTypes.BATCH_EDIT_TIME_RECORDS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/time-records`,
        method: "PUT",
        body: { ids, attributes },
      }),
    payload: {
      projectId,
      ids,
      ...attributes,
      schema: schemaTimeRecords.arrayOfTimeRecords,
    },
  };
};

export const updatedTimeRecords = (data) => {
  return {
    type: actionTypes.UPDATED_TIME_RECORDS,
    response: normalize(data, arrayOfTimeRecords),
  };
};
